/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text } from '@components/atoms'
import { IconButton, Tooltip, Chip, Button } from '@mui/material'
import { Close, DragIndicator, WarningAmber } from '@mui/icons-material'
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd'

import { SeasonType } from '@schemas'
import { useAppDispatch } from '@redux/index'
import { seasonActions } from '@redux/store/index'
import { green, grey, orange } from '@mui/material/colors'
import dayjs from 'dayjs'

interface PropsType {
	/** 대기열에 있는 시즌 (queueOrder 오름차순) */
	queueList: SeasonType[]
	selectedSeasonId: string
	onSeasonClick: (season: SeasonType) => void
	onOpenClick: (season: SeasonType) => void
}

/**
 * 판매 대기열.
 *
 * 순서는 "다음에 열 순서"를 뜻할 뿐 자동 전환을 예약하지 않는다.
 * 실제 전환은 사람이 누르고, 판매 기간 안에서만 가능하다.
 * (SEASON_OPEN_PLAN.md §7)
 */
export const SeasonQueueList = (props: PropsType) => {
	const { queueList, selectedSeasonId, onSeasonClick, onOpenClick } = props
	const reduxDispatch = useAppDispatch()

	/**
	 * 앞 순번인데 시작일이 더 늦은 시즌이 있으면 날짜 역전이다.
	 * 드래그 중에는 모달 대신 인라인 배지로만 알린다.
	 */
	const invertedIdSet: Set<string> = React.useMemo(() => {
		const res = new Set<string>()

		queueList.forEach((aSeason, idx) => {
			const hasLaterAhead = queueList
				.slice(0, idx)
				.some(anEarlier => dayjs(anEarlier.startDate).isAfter(dayjs(aSeason.startDate)))

			if (hasLaterAhead) {
				res.add(aSeason.id)
			}
		})

		return res
	}, [queueList])

	const onDragEnd = (result: DropResult) => {
		if (!result.destination || result.destination.index === result.source.index) {
			return
		}

		const newList = [...queueList]
		const [moved] = newList.splice(result.source.index, 1)
		newList.splice(result.destination.index, 0, moved)

		reduxDispatch(seasonActions.UPDATE_SEASON_QUEUE(newList.map(aSeason => aSeason.id)))
	}

	const onRemoveClick = (seasonId: string) => {
		reduxDispatch(
			seasonActions.UPDATE_SEASON_QUEUE(
				queueList.filter(aSeason => aSeason.id !== seasonId).map(aSeason => aSeason.id),
			),
		)
	}

	if (queueList.length === 0) {
		return (
			<View css={style}>
				<View className="emptyView">
					<Text className="emptyText">판매 대기열이 비어 있습니다.</Text>
					<Text className="emptyHint">
						시즌 목록에서 &ldquo;대기열에 추가&rdquo;를 눌러 다음에 열 시즌을
						준비해두세요.
					</Text>
				</View>
			</View>
		)
	}

	return (
		<View css={style}>
			<DragDropContext onDragEnd={onDragEnd}>
				<Droppable droppableId="seasonQueue">
					{droppableProvided => (
						<div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
							{queueList.map((aSeason, idx) => {
								const isInverted = invertedIdSet.has(aSeason.id)
								const isInSalePeriod =
									!dayjs().startOf('day').isBefore(dayjs(aSeason.startDate)) &&
									!dayjs().startOf('day').isAfter(dayjs(aSeason.endDate))

								return (
									<Draggable key={aSeason.id} draggableId={aSeason.id} index={idx}>
										{(draggableProvided, snapshot) => (
											<div
												ref={draggableProvided.innerRef}
												{...draggableProvided.draggableProps}
												className={`queueItem ${snapshot.isDragging ? 'dragging' : ''} ${
													selectedSeasonId === aSeason.id ? 'selected' : ''
												}`}
												onClick={() => onSeasonClick(aSeason)}
											>
												<div
													className="dragHandle"
													{...draggableProvided.dragHandleProps}
												>
													<DragIndicator fontSize="small" />
												</div>

												<span className="orderBadge">{idx + 1}</span>

												<div className="queueContent">
													<div className="queueHeader">
														<Text className="queueTitle">{aSeason.title}</Text>
														{isInverted && (
															<Tooltip title="앞 순번에 더 늦게 시작하는 시즌이 있습니다">
																<Chip
																	icon={<WarningAmber />}
																	label="순서 확인"
																	size="small"
																	className="invertedChip"
																/>
															</Tooltip>
														)}
													</div>
													<Text className="queueDate">
														{`${dayjs(aSeason.startDate).format('YYYY.MM.DD')} - ${dayjs(
															aSeason.endDate,
														).format('YYYY.MM.DD')}`}
													</Text>

													<Button
														size="small"
														variant="contained"
														color="success"
														className="openButton"
														disabled={!isInSalePeriod}
														onClick={e => {
															e.stopPropagation()
															onOpenClick(aSeason)
														}}
													>
														{isInSalePeriod ? '판매 시작' : '기간 아님'}
													</Button>
												</div>

												<Tooltip title="대기열에서 제거">
													<IconButton
														size="small"
														className="removeButton"
														onClick={e => {
															e.stopPropagation()
															onRemoveClick(aSeason.id)
														}}
													>
														<Close fontSize="small" />
													</IconButton>
												</Tooltip>
											</div>
										)}
									</Draggable>
								)
							})}
							{droppableProvided.placeholder}
						</div>
					)}
				</Droppable>
			</DragDropContext>
		</View>
	)
}

const style = css({
	width: '100%',

	'.emptyView': {
		padding: '2rem 1rem',
		textAlign: 'center',

		'.emptyText': {
			display: 'block',
			marginBottom: '0.5rem',
			color: grey[700],
			fontWeight: 600,
		},

		'.emptyHint': {
			display: 'block',
			color: grey[500],
			fontSize: '0.85rem',
			lineHeight: 1.6,
		},
	},

	'.queueItem': {
		position: 'relative',
		display: 'flex',
		alignItems: 'flex-start',
		gap: '0.5rem',
		marginBottom: '0.75rem',
		padding: '0.75rem',
		background: 'white',
		borderRadius: '5px',
		borderLeft: '10px solid',
		borderLeftColor: grey[300],
		boxShadow: '0px 2px 5px 0px rgb(0 0 0 / 25%)',
		cursor: 'pointer',
		transition: 'all 0.2s ease-in-out',

		'&:hover': {
			backgroundColor: '#f9fafb',
		},

		'&.dragging': {
			boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
		},

		'&.selected': {
			borderLeftColor: green['A700'],
			background: '#f1f8f4',
		},

		'.dragHandle': {
			display: 'flex',
			alignItems: 'center',
			height: '1.75rem',
			color: grey[400],
			cursor: 'grab',

			'&:active': {
				cursor: 'grabbing',
			},
		},

		'.orderBadge': {
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			flexShrink: 0,
			width: '1.5rem',
			height: '1.5rem',
			marginTop: '0.1rem',
			background: grey[800],
			color: 'white',
			borderRadius: '50%',
			fontSize: '0.75rem',
			fontWeight: 700,
		},

		'.queueContent': {
			flex: 1,
			minWidth: 0,
			paddingRight: '1.5rem',

			'.queueHeader': {
				display: 'flex',
				alignItems: 'center',
				gap: '0.5rem',
				marginBottom: '0.15rem',

				'.queueTitle': {
					margin: 0,
					fontSize: '1.05rem',
					fontWeight: 600,
					color: '#111827',
				},

				'.invertedChip': {
					background: orange[50],
					color: orange[900],
					fontWeight: 700,
					fontSize: '0.7rem',

					'.MuiChip-icon': {
						color: orange[700],
					},
				},
			},

			'.queueDate': {
				display: 'block',
				margin: 0,
				marginBottom: '0.5rem',
				color: grey[600],
				fontSize: '0.85rem',
			},

			'.openButton': {
				fontWeight: 700,
			},
		},

		'.removeButton': {
			position: 'absolute',
			top: '0.5rem',
			right: '0.5rem',
		},
	},
})
